import React, { useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';

import * as LoyaltyService from '~/services/LoyaltyService';

import {
  type ManualAccrual,
  type ManualAccrualsRetryRequestBody,
  PRODUCT_TYPE_OPTIONS,
  type ProductType,
} from '~/types/loyaltyContract';

interface RetryManualAccrualsDialogProps {
  open: boolean;
  onClose: () => void;
  selectedAccruals?: Array<ManualAccrual>;
  onSuccess?: () => void;
}

export default function RetryManualAccrualsDialog({
  open,
  onClose,
  selectedAccruals = [],
  onSuccess,
}: RetryManualAccrualsDialogProps) {
  const { enqueueSnackbar } = useSnackbar();

  // Form state
  const [selectedProductType, setSelectedProductType] = useState<ProductType | ''>('');
  const [estimateOnly, setEstimateOnly] = useState(false);
  const [force, setForce] = useState(false);
  const [reestimatePendingPoints, setReestimatePendingPoints] = useState(false);
  const [reimportBigQueryData, setReimportBigQueryData] = useState(false);
  const [skipEstimateBeforeAccrual, setSkipEstimateBeforeAccrual] = useState(false);
  const [skipAccrual, setSkipAccrual] = useState(false);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedProductType('');
      setEstimateOnly(false);
      setForce(false);
      setReestimatePendingPoints(false);
      setReimportBigQueryData(false);
      setSkipEstimateBeforeAccrual(false);
      setSkipAccrual(false);
    }
  }, [open]);

  const retryMutation = useMutation({
    mutationFn: (params: ManualAccrualsRetryRequestBody) => LoyaltyService.retryManualAccruals(params),
    onSuccess: (data) => {
      const { result } = data;
      enqueueSnackbar(
        `Retry completed: ${result.totalItemsCount - result.failedItemsCount}/${result.totalItemsCount} successful`,
        { variant: 'success' },
      );
      if (result.failedItemsCount > 0) {
        enqueueSnackbar(`${result.failedItemsCount} items failed to retry`, { variant: 'warning' });
      }
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      enqueueSnackbar(`Error retrying accruals: ${error?.message || 'Unknown error'}`, { variant: 'error' });
    },
  });

  const handleRetry = async (): Promise<void> => {
    const params: ManualAccrualsRetryRequestBody = {
      itemIds: selectedAccruals.length > 0 ? selectedAccruals.map((a) => a.itemId) : undefined,
      productType: selectedProductType || undefined,
      estimateOnly: estimateOnly || undefined,
      force: force || undefined,
      reestimatePendingPoints: reestimatePendingPoints || undefined,
      reimportBigQueryData: reimportBigQueryData || undefined,
      skipEstimateBeforeAccrual: skipEstimateBeforeAccrual || undefined,
      skipAccrual: skipAccrual || undefined,
      useRevisedValidation: true,
    };

    retryMutation.mutate(params);
  };

  const handleClose = (): void => {
    if (!retryMutation.isPending) {
      onClose();
    }
  };

  const itemCount = selectedAccruals.length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Retry Manual Accruals</DialogTitle>
      <DialogContent>
        <Stack spacing={3} mt={1}>
          {itemCount > 0 && (
            <Alert severity="info">
              You have selected {itemCount} accrual{itemCount > 1 ? 's' : ''} to retry.
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Filters
            </Typography>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>Product Type (Optional)</InputLabel>
              <Select
                value={selectedProductType}
                label="Product Type (Optional)"
                onChange={(e: SelectChangeEvent<ProductType | ''>) =>
                  setSelectedProductType(e.target.value as ProductType | '')
                }
              >
                <MenuItem value="">
                  <Typography sx={{ fontStyle: 'italic' }}>All Product Types</Typography>
                </MenuItem>
                {PRODUCT_TYPE_OPTIONS.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Processing Options
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={<Checkbox checked={estimateOnly} onChange={(e) => setEstimateOnly(e.target.checked)} />}
                label="Estimate only (don't actually accrue points)"
              />
              <FormControlLabel
                control={<Checkbox checked={force} onChange={(e) => setForce(e.target.checked)} />}
                label="Force retry (bypass some checks)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reestimatePendingPoints}
                    onChange={(e) => setReestimatePendingPoints(e.target.checked)}
                  />
                }
                label="Re-estimate pending points"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reimportBigQueryData}
                    onChange={(e) => setReimportBigQueryData(e.target.checked)}
                  />
                }
                label="Re-import BigQuery data"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipEstimateBeforeAccrual}
                    onChange={(e) => setSkipEstimateBeforeAccrual(e.target.checked)}
                  />
                }
                label="Skip estimate before accrual"
              />
              <FormControlLabel
                control={<Checkbox checked={skipAccrual} onChange={(e) => setSkipAccrual(e.target.checked)} />}
                label="Skip accrual (estimate only)"
              />
            </Stack>
          </Box>

          {retryMutation.isError && (
            <Alert severity="error">Error: {(retryMutation.error as any)?.message || 'Failed to retry accruals'}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={retryMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleRetry}
          variant="contained"
          color="primary"
          disabled={retryMutation.isPending || itemCount === 0}
        >
          {retryMutation.isPending ? 'Retrying...' : 'Retry Accruals'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
